import * as React from 'react';
import {classNames} from '@shopify/react-utilities/styles';
import {addEventListener, removeEventListener} from '@shopify/javascript-utilities/events';
import {read} from '@shopify/javascript-utilities/fastdom';

import styles from './collapsible.scss';

export interface Props {
  id: string;
  isOpen: boolean;
  children?: React.ReactNode;
}

export type AnimationState =
  | 'idle'
  | 'measuring'
  | 'closingStart'
  | 'closing'
  | 'openingStart'
  | 'opening';

export interface State {
  height?: number | null;
  animationState: AnimationState;
  isOpen?: boolean | null;
}

export class Collapsible extends React.Component<Props, State> {
  static getDerivedStateFromProps(props, state) {
    return props.isOpen === state.isOpen ? {} : {isOpen: props.isOpen, animationState: 'measuring'};
  }

  state: State = {
    height: null,
    animationState: 'idle',
    isOpen: false,
  };

  private node: HTMLElement | null = null;
  private heightNode: HTMLElement | null = null;

  componentDidUpdate({isOpen: wasOpen}: Props) {
    const {animationState} = this.state;

    read(() => {
      switch (animationState) {
        case 'idle':
          break;
        case 'measuring':
          this.setState({
            animationState: wasOpen ? 'closingStart' : 'openingStart',
            height: wasOpen && this.heightNode ? this.heightNode.scrollHeight : 0,
          });
          break;
        case 'closingStart':
          this.setState({
            animationState: 'closing',
            height: 0,
          });
          break;
        case 'openingStart':
          this.setState({
            animationState: 'opening',
            height: this.heightNode ? this.heightNode.scrollHeight : 0,
          });
      }
    });
  }

  componentDidMount() {
    if (this.node == null) {
      return;
    }

    addEventListener(this.node, 'transitionend', this.handleTransitionEnd);
  }

  componentWillUnmount() {
    if (this.node == null) {
      return;
    }

    removeEventListener(this.node, 'transitionend', this.handleTransitionEnd);
  }

  render() {
    const {id, isOpen, children} = this.props;
    const {animationState, height} = this.state;

    const animating = animationState !== 'idle';

    const wrapperClassName = classNames(styles.collapsible, {
      [styles.open]: isOpen,
      [styles.animating]: animating,
      [styles.fullyOpen]: !animating && isOpen,
    });

    const displayHeight = collapsibleHeight(isOpen, animationState, height);

    const content = animating || isOpen ? children : null;

    return (
      <div
        id={id}
        aria-hidden={!isOpen}
        style={{height: displayHeight}}
        className={wrapperClassName}
        ref={this.bindNode}
      >
        <div ref={this.bindHeightNode}>{content}</div>
      </div>
    );
  }

  private bindNode = (node: HTMLElement | null) => {
    this.node = node;
  };

  private bindHeightNode = (node: HTMLElement | null) => {
    this.heightNode = node;
  };

  private handleTransitionEnd = (event: Event) => {
    const {target} = event;
    if (target === this.node) {
      this.setState({animationState: 'idle', height: null});
    }
  };
}

function collapsibleHeight(
  isOpen: boolean,
  animationState: AnimationState,
  height?: number | null,
) {
  if (animationState === 'idle' && isOpen) {
    return isOpen ? 'auto' : undefined;
  }

  if (animationState === 'measuring') {
    return isOpen ? undefined : 'auto';
  }

  return `${height || 0}px`;
}

export default Collapsible;
